import { Id } from '@/infra/id'
import type { TranslationResource } from '@/features/iam/api/resources/translations'
import { type Observable, switchMap } from 'rxjs'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type { TranslationApi } from '@/features/iam/api/iam-api'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import type { DefaultPreselectedWorkspaceTranslationsProvider } from '@/features/iam/translations/PreselectedWorkspaceTranslationsProvider'
import { ObservableMap } from 'mobx'

type WorkspaceId = Id
type Locale = string
type TranslationKey = string
type TranslationValue = string
type TranslationMap = ObservableMap<TranslationKey, TranslationValue>

export class TranslationStore {
  private _translations = new ObservableMap<Locale, TranslationMap>()

  constructor(
    private readonly _api: TranslationApi,
    _preselectedWorkspaceTranslationsProvider: DefaultPreselectedWorkspaceTranslationsProvider,
    currentWorkspace$: Observable<Workspace>,
  ) {
    const serverTranslations = _preselectedWorkspaceTranslationsProvider.read()

    if (!serverTranslations) {
      currentWorkspace$
        .pipe(
          switchMap((workspace) =>
            withAbortSignal((signal) =>
              this.fetchTranslations(workspace.id, signal),
            ),
          ),
          shareLatest(),
        )
        .subscribe()
    } else {
      this._setupTranslations(serverTranslations)
    }

    this.translate = this.translate.bind(this)
  }

  translate(key: string, defaultValue: string, locale = 'en_us') {
    return this._translations.get(locale)?.get(key) || defaultValue
  }

  async fetchTranslations(workspaceId: WorkspaceId, signal?: AbortSignal) {
    const translations = await this._api.getAll(Id.assert(workspaceId), signal)
    signal?.throwIfAborted()
    this._setupTranslations(translations)
  }

  private _setupTranslations(translations: TranslationResource[]) {
    for (const translation of translations) {
      const locale = translation.locale
      const key = translation.key
      const value = translation.value

      let localeMap = this._translations.get(locale)
      if (!localeMap) {
        localeMap = new ObservableMap()
        this._translations.set(locale, localeMap)
      }

      localeMap.set(key, value)
    }
  }
}
